/**
 * signedOutEntry.js — paths an unsigned visitor may open.
 * Shaped by: SCL-082; md3c.com Step 2.
 * Invariants: every host except md3c.com and fusionmsi.com keeps the exact-path
 * allowlist. On the md3c host, MD3C_PUBLIC_PATHS is added. On fusionmsi.com,
 * only FusionMSI marketing paths are added. Query, hash, and a trailing
 * slash do not create a new path. Do not add /get-on-the-map to the md3c
 * list. fusionmsi.com serves it through isFusionMsiPublicMarketingPath.
 * Related: src/routes/AppRoutes.jsx,
 * src/features/md3c/marketing/md3cMarketingRouting.js,
 * src/features/decisionPoint/marketing/fusionMsiMarketingRouting.js.
 */

import { isMd3cMarketingHost } from "features/md3c/marketing/md3cMarketingRouting";
import {
  isExactFusionMsiHost,
  isFusionMsiHost,
  isFusionMsiPublicMarketingPath,
} from "features/decisionPoint/marketing/fusionMsiMarketingRouting";

const SIGNED_OUT_EXACT_PATHS = new Set([
  "/auth",
  "/signup",
  "/account",
  "/voxpont-auth",
  "/dp-auth",
  "/privacy-policy",
  "/terms-of-use",
  "/baa",
  "/voxpont-terms",
  "/voxpont-privacy",
  "/voxpont-baa",
]);

/** Public paths on the md3c host only. /get-on-the-map is not one of them. */
export const MD3C_PUBLIC_PATHS = Object.freeze({
  exact: Object.freeze([
    "/",
    "/critical-challenges",
    "/about-us",
  ]),
  prefixes: Object.freeze(["/care-before-the-visit/"]),
});

const MD3C_PUBLIC_EXACT = new Set(MD3C_PUBLIC_PATHS.exact);

export function normalizeEntryPath(pathname) {
  const raw = String(pathname || "/")
    .split("?")[0]
    .split("#")[0];
  if (raw.length > 1 && raw.endsWith("/")) return raw.slice(0, -1);
  return raw || "/";
}

function isMd3cPublicPath(pathname) {
  const path = normalizeEntryPath(pathname);
  if (MD3C_PUBLIC_EXACT.has(path)) return true;
  return MD3C_PUBLIC_PATHS.prefixes.some((prefix) => {
    const base = prefix.endsWith("/") ? prefix.slice(0, -1) : prefix;
    return path === base || path.startsWith(`${base}/`);
  });
}

/**
 * @param {string} pathname
 * @param {string} [hostname] When omitted, the md3c helper reads the window host.
 */
function onFusionMsiMarketingHost(hostname) {
  if (hostname === undefined) return isFusionMsiHost();
  return isExactFusionMsiHost(hostname);
}

export function isSignedOutEntryPath(pathname, hostname) {
  const onMd3cHost =
    hostname === undefined ? isMd3cMarketingHost() : isMd3cMarketingHost(hostname);
  if (onMd3cHost && isMd3cPublicPath(pathname)) return true;
  if (onFusionMsiMarketingHost(hostname) && isFusionMsiPublicMarketingPath(pathname)) {
    return true;
  }
  return SIGNED_OUT_EXACT_PATHS.has(normalizeEntryPath(pathname));
}

function signInBase(hostname) {
  const host = String(hostname || "").toLowerCase();
  if (host === "voxpont.com" || host.endsWith(".voxpont.com")) {
    return "/voxpont-auth";
  }
  return "/auth";
}

function safeNextPath(pathname, search, hostname) {
  const path = normalizeEntryPath(pathname);
  if (!path || path === "/" || isSignedOutEntryPath(path, hostname)) return null;
  const next = `${path}${search || ""}`;
  if (!next.startsWith("/") || next.startsWith("//")) return null;
  if (next.includes("://") || next.includes("\\")) return null;
  return next;
}

/**
 * Sign-in URL for an unsigned visitor. Product paths are kept as `next`
 * so a successful sign-in can return there. The site root is not.
 */
export function signedOutSignInTarget({
  pathname = "/",
  search = "",
  hostname = "",
} = {}) {
  const base = signInBase(hostname);
  const next = safeNextPath(pathname, search, hostname);
  if (!next) return base;
  return `${base}?next=${encodeURIComponent(next)}`;
}
